import { Num, RelativeTime, Tag } from '@bookora/ui';

import type { AnnouncementSeverity, StaffNotification } from '@/api/types';

import type { NotificationGroup } from './notification-groups';

const SEVERITY_COLOR: Record<AnnouncementSeverity, string | undefined> = {
  INFO: undefined,
  SUCCESS: 'success',
  WARNING: 'warning',
  CRITICAL: 'error',
};

/**
 * One notification as a real `<button>`: title, type tag, body, age. Shared
 * by the popover and the full list so the two cannot look different.
 */
export function NotificationRow({
  item,
  onOpen,
}: {
  item: StaffNotification;
  onOpen: (item: StaffNotification) => void;
}) {
  return (
    <button
      type="button"
      className={`bk-notification-row bk-pressable${item.isRead ? '' : ' bk-notification-row--unread'}`}
      onClick={() => onOpen(item)}
    >
      <span className="bk-notification-row__title">
        <span>{item.title}</span>
        {SEVERITY_COLOR[item.severity] ? (
          <Tag color={SEVERITY_COLOR[item.severity]}>{item.typeLabelFa}</Tag>
        ) : (
          <Tag>{item.typeLabelFa}</Tag>
        )}
        {item.isRead ? null : <span className="bk-visually-hidden">خوانده‌نشده</span>}
      </span>
      {item.body ? <span className="bk-muted bk-notification-row__body">{item.body}</span> : null}
      <span className="bk-subtle bk-notification-row__time">
        <RelativeTime value={item.createdAt} />
      </span>
    </button>
  );
}

/**
 * Several stock notices of one type, folded into one row (see
 * `notification-groups.ts`). Same shape as a single row, so the list does not
 * change rhythm where a group sits.
 */
export function NotificationGroupRow({
  group,
  onOpen,
}: {
  group: NotificationGroup;
  onOpen: (group: NotificationGroup) => void;
}) {
  const unread = group.items.filter((item) => !item.isRead).length;
  return (
    <button
      type="button"
      className={`bk-notification-row bk-pressable${group.isRead ? '' : ' bk-notification-row--unread'}`}
      onClick={() => onOpen(group)}
    >
      <span className="bk-notification-row__title">
        <span>
          <Num value={group.items.length} /> {group.noun}
        </span>
        <Tag color="warning">{group.typeLabelFa}</Tag>
        {unread > 0 ? (
          <span className="bk-visually-hidden">
            <Num value={unread} /> خوانده‌نشده
          </span>
        ) : null}
      </span>
      <span className="bk-muted bk-notification-row__body">
        جزئیات و روش رسیدگی به هر مورد رو در بخش «هشدارهای انبار» ببینید.
      </span>
      <span className="bk-subtle bk-notification-row__time">
        <RelativeTime value={group.createdAt} />
      </span>
    </button>
  );
}
